import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { Observable, skip, take, takeUntil } from "rxjs";
import { BaseComponent } from "src/app/base.component";
import { Constants } from "src/app/shared/data/constants";
import { Authorities } from "src/app/shared/enum/authorities";
import { AudienceType } from "src/app/shared/enum/chart/audiance";
import { RefreshButtonState } from "src/app/shared/enum/refreshButtonState";
import { FranchiseProfileInterface } from "src/app/shared/models/franchise/franchise-profile.interface";
import { FranchiseMainDashboardDto } from "src/app/shared/models/franchise/FranchiseMainDashboardDto";
import { InputAudience } from "src/app/shared/models/input-audience.interface";
import { JwtService } from "src/app/shared/services/jwt.service";
import { AppStateInterface } from "src/app/store/appState.interface";
import {
  invokeFranchiseInfo,
  invokeMainDashboard,
} from "src/app/store/franchise/franchise.action";
import {
  resultFranchiseInfoSelector,
  resultLoadingMainDashboardSelector,
  resultMainDashboardSelector,
} from "src/app/store/franchise/franchise.selectors";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-main",
  templateUrl: "./main.component.html",
  styleUrls: ["./main.component.scss"],
})
export class MainComponent extends BaseComponent implements OnInit {
  cachedAt: Date | null;
  views: number = 0;
  forbidden: boolean = false;
  fetchingMainDashboardState: RefreshButtonState = RefreshButtonState.IDLE;

  mainDashboard$: Observable<FranchiseMainDashboardDto | null>;
  mainDashboard: FranchiseMainDashboardDto = {
    advertisingCampaigns: 0,
    cachedAt: null,
    locations: 0,
    retainedConsumers: 0,
    routers: 0,
    uniqueConsumers: 0,
    views: 0,
    reach: 0,
  };

  impressionsCard = {
    title: "dashboard.location.viewsNumber",
    icon: "assets/identity/eye-icon.svg",
    class: "photo",
  };
  uniqueConsumersCard = {
    title: "dashboard.location.uniqueUsers",
    icon: "assets/identity/uniqueConsumers.svg",
    class: "card info-card",
  };
  reachCard = {
    title: "dashboard.location.reach",
    icon: "assets/identity/uniqueViews.svg",
    class: "card info-card",
  };
  locationsCard = {
    title: "dashboard.admin.cards.lps",
    icon: "assets/identity/icon-lp.svg",
    class: "card info-card",
  };
  routersCard = {
    title: "dashboard.location.installedRoutersNumber",
    icon: "assets/identity/icon-router.svg",
    class: "card info-card",
  };
  adsCard = {
    title: "dashboard.admin.cards.ads",
    icon: "assets/identity/ad.svg",
    class: "card info-card",
  };

  companyName: string;
  @Output() inputAudience = new EventEmitter<InputAudience>();

  fetchingLocationPartnerPreviewState: RefreshButtonState =
    RefreshButtonState.IDLE;

  franchiseProfile$: Observable<FranchiseProfileInterface | null>;
  firstName : string = "";
  lastName: string = "";
  email : string
  loadingMainDashboardSelector$: Observable<boolean>

  bookMeeting = environment.links.bookMeetingLP;

  today = new Date();
  startDate = Constants.twoMonthsAgo;
  constructor(
    private store: Store<AppStateInterface>,
    private jwtService: JwtService
  ) {
    super(store);
    this.mainDashboard$ = this.store
      .pipe(select(resultMainDashboardSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    this.franchiseProfile$ = this.store
      .pipe(select(resultFranchiseInfoSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    this.loadingMainDashboardSelector$ = this.store
      .pipe(select(resultLoadingMainDashboardSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    const authorities = this.jwtService.getPermissions(jwtService.getToken());
    if (authorities) {
      this.forbidden = !authorities.includes(Authorities.FR_MAIN.toString());
    }
    this.companyName = this.jwtService.getCompanyName();
  }

  ngOnInit(): void {
    this.fetchMain(false);
    this.mainDashboard$.subscribe((result) => {
      if (result) {
        this.mainDashboard = result;
        this.cachedAt = result.cachedAt;
      }
    });
    this.franchiseProfile$.subscribe(
      (result: FranchiseProfileInterface | null) => {
        if (result !== null) {
          this.firstName = result.firstName;
          this.lastName = result.lastName;
          this.email = result.email
          this.inputAudience.emit({
            name: this.companyName,
            type: AudienceType.FRANCHISE,
            title: "dashboard.location.summary",
            titleSpline: "invoices.summary",
            titleColumnChart: "invoices.distributionAgeGender",
            email: this.email
          });
        } else {
          this.store.dispatch(invokeFranchiseInfo());
        }
      }
    );
    this.store.dispatch(invokeFranchiseInfo());
  }

  onClickRefreshCache() {
    this.fetchingLocationPartnerPreviewState = RefreshButtonState.LOADING;
    this.fetchMain(true);
  }

  fetchMain(refreshCache: boolean) {
    if (refreshCache) {
      this.mainDashboard$.pipe(skip(1), take(1)).subscribe((result) => {
        if (result) {
          this.fetchingLocationPartnerPreviewState = RefreshButtonState.SUCCESS;
          setTimeout(
            () =>
              (this.fetchingLocationPartnerPreviewState =
                RefreshButtonState.IDLE),
            2500
          );
        } else {
          this.fetchingLocationPartnerPreviewState = RefreshButtonState.LOADING;
        }
      });
    }
    this.store.dispatch(invokeMainDashboard({ refreshCache: refreshCache }));
  }
}
